import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

const FacebookContext = createContext();

export const useFacebook = () => useContext(FacebookContext);

const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID;
const FACEBOOK_REDIRECT_URI = import.meta.env.VITE_FACEBOOK_REDIRECT_URI;
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

// Facebook Pages scopes
const SCOPES = [
  'pages_show_list',
  'pages_read_engagement',
  'pages_read_user_content',
  'pages_manage_posts',
  'pages_manage_engagement',
  'pages_manage_metadata',
  'read_insights',
  'business_management',
].join(',');

const generateRandomString = (length = 32) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
    .substring(0, length);
};

export const FacebookProvider = ({ children }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const user = useSelector((state) => state.auth?.user);

  useEffect(() => {
    if (user) {
      loadConnectedAccounts();
    } else {
      setAccounts([]);
      setLoading(false);
    }
  }, [user]);

  const loadConnectedAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/facebook/accounts`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error('Failed to load Facebook Pages:', err);
    } finally {
      setLoading(false);
    }
  };

  const connectFacebook = useCallback(() => {
    try {
      const state = `fb_${generateRandomString(32)}`;
      sessionStorage.setItem('facebook_oauth_state', state);

      const authUrl = new URL('https://www.facebook.com/v21.0/dialog/oauth');
      authUrl.searchParams.set('client_id', FACEBOOK_APP_ID);
      authUrl.searchParams.set('redirect_uri', FACEBOOK_REDIRECT_URI);
      authUrl.searchParams.set('scope', SCOPES);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('state', state);

      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('Failed to start Facebook Pages OAuth:', err);
      setError('Failed to start Facebook connection');
    }
  }, []);

  const handleCallback = useCallback(async (code, state) => {
    const savedState = sessionStorage.getItem('facebook_oauth_state');
    if (!savedState || state !== savedState) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/facebook/oauth/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code,
          redirect_uri: FACEBOOK_REDIRECT_URI,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to connect Facebook Page');
      }

      const data = await response.json();

      sessionStorage.removeItem('facebook_oauth_state');

      return {
        availableAccounts: data.available_pages,
        tokenRef: data.token_ref,
      };
    } catch (err) {
      sessionStorage.removeItem('facebook_oauth_state');
      throw err;
    }
  }, []);

  const selectAccount = useCallback(async (tokenRef, pageId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/facebook/oauth/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token_ref: tokenRef,
          page_id: pageId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to select Facebook Page');
      }

      const data = await response.json();

      // Dedupe by id: re-connecting a page already linked to this org returns the
      // SAME page (already_connected=true), not a new one — update in place.
      setAccounts(prev => {
        const exists = prev.some(a => a.id === data.account.id);
        return exists
          ? prev.map(a => (a.id === data.account.id ? data.account : a))
          : [...prev, data.account];
      });

      return { account: data.account, alreadyConnected: !!data.already_connected };
    } catch (err) {
      console.error('Failed to select Facebook Page:', err);
      throw err;
    }
  }, []);

  const disconnectAccount = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/facebook/accounts/${accountId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setAccounts(prev => prev.filter(a => a.id !== accountId));
      } else {
        throw new Error('Failed to disconnect page');
      }
    } catch (err) {
      console.error('Failed to disconnect Facebook Page:', err);
      setError(err.message);
    }
  }, []);

  const refreshAccountStats = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/facebook/accounts/${accountId}/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAccounts(prev =>
          prev.map(a => (a.id === accountId ? { ...a, ...data.account } : a))
        );
        return data.account;
      } else {
        throw new Error('Failed to refresh stats');
      }
    } catch (err) {
      console.error('Failed to refresh Facebook Page stats:', err);
      setError(err.message);
    }
  }, []);

  const fetchAccountPosts = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/facebook/accounts/${accountId}/posts`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch posts');
      }
    } catch (err) {
      console.error('Failed to fetch Facebook Page posts:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Create a post on the page (pages_manage_posts)
  const createPost = useCallback(async (accountId, { message, link }) => {
    const response = await fetch(`${API_BASE_URL}/facebook/accounts/${accountId}/posts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ message, link }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create post');
    }
    return response.json();
  }, []);

  // Get comments for a post (pages_manage_engagement)
  const fetchPostComments = useCallback(async (accountId, postId) => {
    const response = await fetch(`${API_BASE_URL}/facebook/accounts/${accountId}/posts/${postId}/comments`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch comments');
    }
    return response.json();
  }, []);

  // Reply to a comment as the page (pages_manage_engagement)
  const replyToComment = useCallback(async (accountId, commentId, message) => {
    const response = await fetch(`${API_BASE_URL}/facebook/accounts/${accountId}/comments/${commentId}/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ message }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to reply');
    }
    return response.json();
  }, []);

  // Delete a comment (pages_manage_engagement)
  const deleteComment = useCallback(async (accountId, commentId) => {
    const response = await fetch(`${API_BASE_URL}/facebook/accounts/${accountId}/comments/${commentId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete comment');
    }
    return response.json();
  }, []);

  // Fetch page insights (read_insights)
  const fetchInsights = useCallback(async (accountId, period = 'day') => {
    const response = await fetch(`${API_BASE_URL}/facebook/accounts/${accountId}/insights?period=${period}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch insights');
    }
    return response.json();
  }, []);

  // Update page metadata (pages_manage_metadata)
  const updatePageMetadata = useCallback(async (accountId, fields) => {
    const response = await fetch(`${API_BASE_URL}/facebook/accounts/${accountId}/metadata`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(fields),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update page');
    }
    const data = await response.json();
    setAccounts(prev =>
      prev.map(a => (a.id === accountId ? { ...a, ...data.account } : a))
    );
    return data;
  }, []);

  const getAccountById = useCallback((accountId) => {
    return accounts.find(a => a.id === accountId);
  }, [accounts]);

  return (
    <FacebookContext.Provider
      value={{
        accounts,
        loading,
        error,
        connectFacebook,
        handleCallback,
        selectAccount,
        disconnectAccount,
        refreshAccountStats,
        loadConnectedAccounts,
        fetchAccountPosts,
        getAccountById,
        createPost,
        fetchPostComments,
        replyToComment,
        deleteComment,
        fetchInsights,
        updatePageMetadata,
      }}
    >
      {children}
    </FacebookContext.Provider>
  );
};

export default FacebookContext;
